/**
 * 菜单模型 —— 纯逻辑。
 *
 * 为什么把菜单做成数据而不是直接写模板：
 *
 * 1. **同一个模型喂给多个出口。** 右键菜单、工具栏按钮、快捷键，
 *    三处要展示的其实是同一批操作。写三遍必然不同步。
 * 2. **可用性可算。** 「没选中节点时，对齐那一组整体不出现」这类判断
 *    放在纯函数里，可以单测。
 * 3. **键盘导航需要拍平后的序列。** 分组只是视觉，上下键要跨分组移动。
 */

export interface MenuItem {
  id: string
  label: string
  /** 右侧的快捷键提示，如 `⌘Z` */
  hint?: string
  disabled?: boolean
  /** 危险操作：渲染成红色 */
  danger?: boolean
  /**
   * 勾选态（菜单栏里的窗口开关用）。
   *
   * 显式字段而不是在 label 前面拼「✓」—— 拼字符串的话，
   * 搜索、快捷键提示、无障碍标签全都会把那个符号当成名字的一部分。
   */
  checked?: boolean
  /** 没有 run 的条目只展示（比如纯说明） */
  run?: () => void
  /**
   * 图标（一段内联 SVG 的 path/shape 字符串，`innerHTML` 进去）。
   *
   * 有图标的项在 `kind: 'toolbar'` 的分组里渲染成**方图标按钮**
   * （学 Win11 / 浏览器 DevTools 的 flex 对齐小工具那种）；
   * 在普通列表里图标只是左侧的一个前置符号。
   *
   * ⚠️ 图标**不替代** label：`label` 仍然会进 `title` / `aria-label`，
   * 也仍然参与搜索。只有图标的按钮对读屏和「我该点哪个」都是灾难。
   */
  icon?: string
  /**
   * 额外的搜索关键词。
   *
   * 菜单项一多就必须能搜 —— 逐层展开找「条形码」比敲三个字慢得多。
   * 但标题往往不够：「组件 · 条形码」应该能被 `barcode`、`条码`、`zpl` 搜到，
   * 所以别名走这里。
   */
  keywords?: string[]
  /** 英文/拼音首字母之类的短标签，搜索命中时显示在右侧 */
  short?: string
  /**
   * 二级菜单。
   *
   * 带 children 的项**不执行 run**，而是展开 ——
   * 「排列」那 8 项平铺在主菜单里会把菜单撑到需要滚动。
   *
   * 目前只支持**一层**（够用了；再深就该考虑换个交互了）。
   */
  children?: MenuSection[]
}

/* ══════════════════════════════════════════════════════════════
   搜索
   ══════════════════════════════════════════════════════════════ */

/**
 * 模糊匹配打分。0 表示不匹配，越大越相关。
 *
 * 分层给分，而不是简单的 `includes`：
 *   · 完全相同 > 前缀 > 词首 > 包含 > 关键词 > 子序列
 * 这样搜「文」时「组件 · 文本」会排在「逻辑 · 列表计数」前面
 * （后者只是碰巧含有这个字）。
 */
export function matchScore(item: MenuItem, query: string): number {
  const q = query.trim().toLowerCase()
  if (!q) return 1

  const label = item.label.toLowerCase()
  // 标题越长，同样命中越不相关 —— 用它当轻量惩罚
  const penalty = Math.min(label.length, 40)

  if (label === q) return 1000
  if (label.startsWith(q)) return 900 - penalty

  // 词首匹配：「逻辑 · 任一为真」用「任一」也能搜到
  const words = label.split(/[\s·、,，/|()（）\[\]-]+/).filter(Boolean)
  if (words.some((w) => w.startsWith(q))) return 750 - penalty
  if (label.includes(q)) return 600 - penalty

  for (const keyword of item.keywords ?? []) {
    const k = keyword.toLowerCase()
    if (k === q) return 520
    if (k.startsWith(q)) return 480
    if (k.includes(q)) return 440
  }

  const short = item.short?.toLowerCase()
  if (short && short.startsWith(q)) return 420

  // 子序列匹配：`lsc` 能命中 `list.count`；间隙越小越相关
  const gap = subsequenceGap(label, q)
  if (gap !== null) return 300 - Math.min(gap, 30)

  return 0
}

/** query 的字符按顺序出现在 text 里时返回「间隙总数」，否则 null */
export function subsequenceGap(text: string, query: string): number | null {
  let cursor = 0
  let gap = 0
  let last = -1

  for (const ch of query) {
    const index = text.indexOf(ch, cursor)
    if (index === -1) return null
    if (last >= 0) gap += index - last - 1
    last = index
    cursor = index + 1
  }
  return gap
}

/**
 * 按 query 过滤 + 排序。
 *
 * 搜索时**拍平成一个分组**（跟 Blender / 命令面板一致）——
 * 结果按相关度排，还按原分组摆放的话，最相关的可能被压在最下面。
 *
 * 空 query 原样返回，所以调用方不用分支。
 */
export function searchMenu(
  sections: readonly MenuSection[],
  query: string,
): MenuSection[] {
  const q = query.trim()
  if (!q) return sections as MenuSection[]

  const scored: { item: MenuItem; score: number }[] = []
  for (const entry of searchableEntries(sections)) {
    // 用「合成了父项关键词」的副本去打分，原项不动
    const score = matchScore({ ...entry.item, keywords: entry.keywords }, q)
    if (score > 0) scored.push({ item: entry.item, score })
  }

  if (scored.length === 0) return []
  // 分数相同时保持原顺序（稳定排序）
  scored.sort((a, b) => b.score - a.score)
  return [{ id: 'search-results', items: scored.map((entry) => entry.item) }]
}

export interface MenuSection {
  id: string
  /** 可选的分组标题 */
  title?: string
  /**
   * 这一组怎么排：
   *   · `'list'`（默认）  竖排、一行一项（文字为主）
   *   · `'toolbar'`       **横排方图标按钮**，一行放得下几个放几个
   *
   * 「撤销 / 重做 / 复制 / 粘贴 / 删除 / 剪切」和「对齐 / 分布 / 尺寸」
   * 这两类都是**看一眼就知道点哪个**的高频动作，做成图标行比排成
   * 6~10 行文字菜单短得多，也不用读字。
   */
  kind?: 'list' | 'toolbar'
  /**
   * 标题怎么呈现：
   *   · `'plain'`（默认）  左对齐的小标题
   *   · `'divider'`        **两侧长横线 + 文字居中**（用户说的「分割组」）
   *
   * 用 `'divider'` 是为了在长菜单里划出**段落**——
   * 「选中节点」「排列」这种标题本身不是操作，是分隔符，
   * 居中带线比左对齐更明确地表达「下面这一组属于我」。
   */
  titleStyle?: 'plain' | 'divider'
  items: MenuItem[]
}

/** 去掉空分组、以及没有任何可点项的分组 —— 菜单不该出现空壳 */
export function pruneMenu(
  sections: readonly MenuSection[],
): MenuSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.label !== ''),
    }))
    .filter((section) => section.items.length > 0)
}

/** 拍平成一个序列（键盘导航用，跨分组移动）。**不递归子菜单** —— 导航是分层的 */
export function flattenItems(
  sections: readonly MenuSection[],
): MenuItem[] {
  return sections.flatMap((section) => section.items)
}

/**
 * 递归拍平（搜索用）。
 *
 * 子菜单里的项**也必须能被搜到** —— 不然「左对齐」藏进二级菜单之后
 * 就搜不到了，那是退步。
 *
 * 子项会**继承父项的关键词**：搜「排列」应该能列出所有对齐方式，
 * 虽然「排列」本身没有 run、不会作为结果出现。
 */
export function searchableEntries(
  sections: readonly MenuSection[],
  inherited: string[] = [],
): { item: MenuItem; keywords: string[] }[] {
  const out: { item: MenuItem; keywords: string[] }[] = []

  for (const section of sections) {
    for (const item of section.items) {
      if (item.children && item.children.length > 0) {
        out.push(
          ...searchableEntries(item.children, [
            ...inherited,
            item.label,
            ...(item.keywords ?? []),
          ]),
        )
        continue
      }
      out.push({ item, keywords: [...(item.keywords ?? []), ...inherited] })
    }
  }
  return out
}

export function menuItemCount(sections: readonly MenuSection[]): number {
  return flattenItems(sections).length
}

export function findMenuItem(
  sections: readonly MenuSection[],
  id: string,
): MenuItem | undefined {
  return flattenItems(sections).find((item) => item.id === id)
}

/**
 * 从 `from` 起按 `step` 找下一个**可点**的项，跳过 disabled。
 *
 * 环回：到底了就从另一头继续（和原生菜单一致）。
 * 一个可点的都没有时返回 -1。
 */
export function nextEnabledIndex(
  items: readonly MenuItem[],
  from: number,
  step: 1 | -1,
): number {
  const total = items.length
  if (total === 0) return -1
  if (!items.some((item) => !item.disabled)) return -1

  let index = from
  for (let guard = 0; guard < total; guard += 1) {
    index = (index + step + total) % total
    if (!items[index]?.disabled) return index
  }
  return -1
}

/** 第一个可点的项（打开菜单时用） */
export function firstEnabledIndex(items: readonly MenuItem[]): number {
  return items.findIndex((item) => !item.disabled)
}
